<template>
  <div
    v-if="racesData"
    :class="[
      $style.raceTable,
      { [$style.raceTableGamePage]: isGamePage, [$style.raceTableInsideModal]: isInsideModal },
    ]"
  >
    <h2 v-if="!isInsideModal" :class="$style.raceTableTitle">{{ leaderboardTitle }}</h2>
    <div v-if="!isCustomTableToShow" :class="$style.raceTableBlock">
      <div :class="$style.raceTableBlockThead">
        <span :class="[$style.raceTabletTdNumber, $style.raceTabletTd, $style.headTitle]">
          {{ racesData.levelTitle }}
        </span>
        <span :class="[$style.raceTabletTdPlayer, $style.raceTabletTd, $style.headTitle]">
          {{ racesData.playerTitle }}
        </span>
        <span :class="[$style.raceTabletTdPlayerData, $style.raceTabletTd, $style.headTitle]">
          {{ mechanicsData.title }}
        </span>
        <span :class="[$style.raceTabletTdPrizes, $style.raceTabletTd, $style.headTitle]">
          {{ racesData.prizeTitle }}
        </span>
      </div>
      <div class="raceTableTbody">
        <BitTableWrapper>
          <div
            v-for="(player, index) of slicedData"
            :key="index"
            :class="$style.raceTableTr"
            :data-is-user="player.isCurrentUser"
            :data-place="index + 1"
          >
            <span :class="[$style.raceTabletTd, $style.raceTabletTdNumber]">
              <span>{{ index + 1 }}</span>
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPlayer]">
              {{ cutNicknameShorter(player.nickname)
              }}{{ player.isCurrentUser ? `(${racesData.leaderboardYou})` : '' }}
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPlayerData]">
              <BitTooltip
                v-if="player[mechanicsData.playerDataKey]"
                is-show-on-hover
                is-tooltip-anchor
                tooltip-width="auto"
                tooltip-position="top-left"
                :tooltip-text="player[mechanicsData.playerDataKey].toLocaleString()"
              >
                <template #tooltipTrigger>
                  <span :class="$style.shortened">
                    {{ player[mechanicsData.playerDataKey].toLocaleString() }}
                  </span>
                </template>
              </BitTooltip>
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPrizes]">
              <span v-if="player.prizes && player.prizes.length">
                <b v-for="prize of player.prizes" :key="prize">
                  {{ prize }}
                </b>
              </span>
              <span v-else>--</span>
            </span>
          </div>
          <div
            v-if="isCurrentPlayerShown && currentPlayerStatus && !isLessButtonShown"
            :class="$style.raceTableTr"
            :data-is-user="true"
          >
            <span :class="[$style.raceTabletTd, $style.raceTabletTdNumber]">
              {{ currentPlayerStatus.award_place || '--' }}
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPlayer]">
              {{ cutNicknameShorter(currentPlayerStatus.nickname) }}({{ racesData.leaderboardYou }})
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPlayerData]">
              <BitTooltip
                v-if="currentPlayerStatus[mechanicsData.playerDataKey]"
                is-show-on-hover
                is-tooltip-anchor
                tooltip-width="auto"
                tooltip-position="top-center"
                :tooltip-text="currentPlayerStatus[mechanicsData.playerDataKey].toLocaleString()"
              >
                <template #tooltipTrigger>
                  <span :class="$style.shortened">
                    {{ currentPlayerStatus[mechanicsData.playerDataKey].toLocaleString() }}
                  </span>
                </template>
              </BitTooltip>
            </span>
            <span :class="[$style.raceTabletTd, $style.raceTabletTdPrizes]">
              <span v-if="currentPlayerStatus.prizes && currentPlayerStatus.prizes.length">
                <b v-for="prize of currentPlayerStatus.prizes" :key="prize"> {{ prize }} </b>
              </span>
              <span v-else>--</span>
            </span>
          </div>
        </BitTableWrapper>
      </div>

      <BitButton
        v-if="isMoreButtonShown || isLessButtonShown"
        :key="triggerUpdate"
        type="secondary"
        is-fluid-width
        :class="[$style.showMoreLessBtn, { [$style.isLessButtonShown]: isLessButtonShown }]"
        @click="toggleShowMore"
      >
        {{ isMoreButtonShown ? showMoreTitle : showLessTitle }}
        <ArrowDownLess />
      </BitButton>
    </div>
    <TableCustomRace
      v-else
      :race-id="raceId"
      :is-game-page="isGamePage"
      :is-network-custom="isNetworkCustom"
      :tournament-extended="tournamentExtended"
    />
  </div>
</template>

<script lang="ts" setup>
import { BitButton, BitTableWrapper, BitTooltip } from '@bit/ui-kit';
import { computed, onMounted, watch } from 'vue';
import TableCustomRace from '~/components/race/tableCustomRace.vue';
import { useRaceTablePagination } from '~/composables/useRaceTablePagination';
import { useSnippet } from '~/composables/useSnippet';
import ArrowDownLess from '~/public/images/common/arrowDownLess.svg?component';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsTournaments } from '~/stores/softswiss/tournaments';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { ERaceMechanics, ERaceStatus, ERaceType } from '~/types/races';
import type { IPlayerTournamentData, ISsTournamentStatus } from '~/types/softswiss/ssTournaments';

interface LeaderboardTableInterface extends IPlayerTournamentData {
  prizes: string[];
  isCurrentUser?: boolean;
}
interface PlayerStatusWithPrizes extends ISsTournamentStatus {
  prizes?: string[];
}

const PLAYER_COUNT_TO_SHOW = 15;

const props = defineProps({
  raceId: {
    type: String,
    requred: true,
    default: '',
  },
  isGamePage: {
    type: Boolean,
    default: false,
  },
  /**
   * if set as true - component will load all data onMount
   */
  init: {
    type: Boolean,
    requred: false,
    default: false,
  },
  isInsideModal: {
    type: Boolean,
    default: false,
  },
});

const storeSsTournaments = useStoreSsTournaments();
const storeComponents = useStoreComponents();
const storeButtons = useStoreButtonTitles();
const snippet = useSnippet();

const tournamentExtended = computed(() =>
  storeSsTournaments.tournamentsExtended.find(
    (t) => t.tournamentData.frontend_identifier?.toLowerCase() === props.raceId
  )
);

const isTournamentActive = computed(
  () => tournamentExtended.value?.tournamentData.status === ERaceStatus.active
);

const isNetworkTournament = computed(
  () => tournamentExtended.value?.raceData.raceType === ERaceType.Network
);

const isSuccessMessageShown = computed(
  () =>
    (storeSsTournaments.findIfSuccessMessageShown(tournamentExtended.value) &&
      isTournamentActive.value) ||
    (isNetworkTournament.value && isTournamentActive.value)
);

const mechanicsData = computed(() => {
  switch (tournamentExtended.value?.raceData.raceMechanics) {
    case ERaceMechanics.Bet:
      return {
        title: storeComponents.races.mechanicsBetTitle,
        playerDataKey: 'bets',
      };
    case ERaceMechanics.Points:
      return {
        title: storeComponents.races.mechanicsPointsTitle,
        playerDataKey: 'points',
      };
    case ERaceMechanics.Rate:
      return {
        title: storeComponents.races.mechanicsRateTitle,
        playerDataKey: 'rate',
      };
    case ERaceMechanics.Spin:
      return {
        title: storeComponents.races.mechanicsSpinTitle,
        playerDataKey: 'games_taken',
      };
    case ERaceMechanics.Win:
      return {
        title: storeComponents.races.mechanicsWinTitle,
        playerDataKey: 'win_cents',
      };
    default:
      return {
        title: '',
        playerDataKey: '',
      };
  }
});

const leaderboardTableAll = computed((): LeaderboardTableInterface[] => {
  if (!tournamentExtended.value) {
    return [];
  }

  const tournament = tournamentExtended.value.tournamentData;
  const customPrizeArray = tournamentExtended.value?.raceData.raceTableCustom;
  const leaderboard = tournament?.top_players?.map((player, index) => {
    const playerPrize = tournament.prizes?.find(
      (prize) => prize.award_place === player.award_place
    );
    const prizes: string[] = [];

    if (playerPrize?.money_award_cents && playerPrize.money_award) {
      const newMoney =
        customPrizeArray[index] !== undefined
          ? customPrizeArray[index].prize
          : customPrizeArray[customPrizeArray.length - 1].prize;
      prizes.push(`${snippet.format(newMoney !== undefined ? newMoney : `0_auto_currency`)}`);
    }

    if (playerPrize?.freespins_count) {
      prizes.push(`${playerPrize?.freespins_count} FS`);
    }

    if (playerPrize?.chargeable_comp_points) {
      prizes.push(`${playerPrize?.chargeable_comp_points} CP`);
    }

    return {
      ...player,
      nickname: `${player.nickname.substring(0, 4)}****`,
      prizes,
    } as LeaderboardTableInterface;
  });

  if (leaderboard) {
    return leaderboard;
  }

  return [];
});

const { isLessButtonShown, isMoreButtonShown, toggleShowMore, triggerUpdate, slicedData, close } =
  useRaceTablePagination<LeaderboardTableInterface[]>(leaderboardTableAll, PLAYER_COUNT_TO_SHOW);

const currentPlayerStatus = computed((): PlayerStatusWithPrizes | undefined => {
  const currentPlayer = storeSsTournaments.tournamentStatus?.find((t) => {
    if (t && t.tournament_id && tournamentExtended.value?.tournamentData) {
      return t.tournament_id === tournamentExtended.value.tournamentData.id;
    }
    return false;
  });
  if (!currentPlayer || !isSuccessMessageShown.value) {
    return undefined;
  }
  const playerNickname = useStoreSsPlayer().playerInfo.nickname;
  if (currentPlayer.award_place) {
    return {
      ...currentPlayer,
      nickname: playerNickname
        ? `${playerNickname.slice(0, 4)}****`
        : `${currentPlayer.nickname.slice(0, 4)}****`,
      prizes: leaderboardTableAll.value[currentPlayer.award_place - 1]?.prizes || '--',
    };
  }

  return {
    ...currentPlayer,
    nickname: playerNickname
      ? `${playerNickname.slice(0, 4)}****`
      : `${currentPlayer.nickname.slice(0, 4)}****`,
  };
});

const isCurrentPlayerShown = computed(() => {
  return (
    currentPlayerStatus.value &&
    !slicedData.value.some(
      (winner) => winner.award_place === currentPlayerStatus.value?.award_place
    )
  );
});

const isNetworkRace = computed(
  () => tournamentExtended.value?.raceData.raceType === ERaceType.Network
);
const isNetworkCustom = computed(
  () =>
    isNetworkRace.value &&
    tournamentExtended.value?.raceData.raceMechanics === ERaceMechanics.Special
);

const leaderboardTitle = computed(() =>
  isNetworkCustom.value
    ? storeComponents.races.networkRaceLeaderboardTitle
    : storeComponents.races.leaderboardTitle
);

const isCustomTableToShow = computed(() => isNetworkRace.value || !slicedData.value.length);

const cutNicknameShorter = (value: string) => {
  return props.isGamePage ? `${value.slice(0, 4)}*` : value;
};

watch(
  () => props.raceId,
  () => {
    close();
  }
);

// watch(isCurrentPlayerShown, () => {
//   if (props.isGamePage && !playersCountToShow.value) {
//     updatePlayerCount(1, true);
//   }
// });

onMounted(async () => {
  if (props.init) {
    await storeSsTournaments.loadTournaments();
  }
});

const racesData = computed(() => storeComponents.races);
const showMoreTitle = computed(() => storeButtons.showMoreGames);
const showLessTitle = computed(() => storeButtons.showLessGames);
</script>

<style lang="scss" module src="./table.scss"></style>
